'use client';

import { useEffect, Children, useRef, useCallback } from 'react';
import Image from '../image';
import { useSpring, animated, config, useSpringValue } from '@react-spring/web';
import { useSpringCarousel, useMedia } from '@ssv-website/hooks';

interface ITabsTitleProps {
  title: string;
  imageUrl?: string;
  subTitle?: string;
  value?: number | string;
  active?: boolean;
  onClick?: () => void;
  count?: number;
  themeStyle?: {
    color: string;
    backgroundColor: string;
  };
  disabledAnimated?: boolean;
  childrenCount?: number;
  isStop?: boolean;
  onStop?: () => void;
  onStart?: () => void;
}

interface ITabsPaneProps extends ITabsTitleProps {
  children?: React.ReactNode;
}

interface ITabsProps {
  activeKey?: string;
  children: React.ReactElement[];
  themeStyle?: {
    color: string;
    backgroundColor: string;
  };
  disabledAnimated?: boolean;
  //优化
  watchChildren?: boolean;
}
const DURATION = 10000;
const useAutoTabItem = (
  active: boolean,
  disabledAnimated?: boolean,
  isStop?: boolean
) => {
  const isMobile = useMedia('mobile');
  const animatedProps = {
    from: {
      x: '-100%',
    },
    to: {
      x: '0%',
    },
    config: {
      ...config.slow,
      duration: isMobile || disabledAnimated ? 500 : DURATION,
    },
    onRest() {},
  };
  const [styles, api] = useSpring(() => animatedProps);

  useEffect(() => {
    api.start(animatedProps);
  }, [active, isMobile]);

  // useEffect(() => {
  //   if (isStop) {
  //     api.pause();
  //   } else {
  //     api.resume();
  //   }
  // }, [isStop]);

  return styles;
};

const useChildHeightToParent = (watchChildren = false, deps: any[] = []) => {
  const nodeRef = useRef<any>(null);
  const ref = useCallback((node: HTMLDivElement) => {
    if (node) {
      nodeRef.current = node;
    }
  }, []);
  const height = useSpringValue(0);
  const adjustParentHeight = () => {
    if (nodeRef.current) {
      const rect = nodeRef.current.getBoundingClientRect();
      if (rect.height > 0) {
        height.set(rect.height);
      }
    }
  };
  useEffect(() => {
    adjustParentHeight();
  }, deps);

  useEffect(() => {
    if (!nodeRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.target === nodeRef.current) {
          adjustParentHeight();
        }
      }
    });

    // 启动观察
    resizeObserver.observe(nodeRef.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, [watchChildren, ...deps]);

  return {
    ref,
    height,
  };
};

const TabsTitle: React.FC<ITabsTitleProps> = ({
  imageUrl,
  title,
  subTitle,
  active = false,
  onClick,
  count = 0,
  themeStyle,
  disabledAnimated = false,
  childrenCount = 0,
  isStop,
  onStart,
  onStop,
}) => {
  const styles = useAutoTabItem(active, disabledAnimated, isStop);
  const isMobile = useMedia('mobile');
  const cls =
    isMobile && childrenCount > 2
      ? 'first:rounded-tl-md last:rounded-br-md mobile:[&:nth-child(2)]:rounded-tr-md mobile:[&:nth-last-child(2)]:rounded-bl-md'
      : 'first:rounded-l-md last:rounded-r-md';
  const groupCls =
    isMobile && childrenCount > 2
      ? 'group-first:rounded-tl-md group-last:rounded-br-md mobile:group-[&:nth-child(2)]:rounded-tr-md mobile:group-[&:nth-last-child(2)]:rounded-bl-md'
      : 'group-first:rounded-l-md group-last:rounded-r-md';
  return (
    <div
      className={`group flex-1 overflow-hidden relative bg-white shadow-card ${cls}`}
      onClick={onClick}
      onMouseEnter={onStop}
      onMouseLeave={onStart}
    >
      <div
        className={`h-[54px] flex flex-col justify-center items-center py-4 border-white rounded-md cursor-pointer mobile:h-[42px]`}
      ></div>
      <div
        className={`absolute left-0 top-0 w-full h-full border-[2px] border-white overflow-hidden ${groupCls}`}
      >
        <animated.div
          className={`w-full h-full ${active ? 'bg-btn' : ''}`}
          style={{
            ...styles,
            backgroundColor: active ? themeStyle?.backgroundColor : undefined,
          }}
        ></animated.div>
      </div>
      <div className="absolute left-0 top-0 w-full h-full">
        <div
          className={`flex flex-col justify-center items-center py-4 border-white rounded-md cursor-pointer mobile:py-3`}
        >
          <span
            className={`text-base -leading-[1px] line-clamp-1 mobile:text-xs ${
              active ? 'text-active' : 'text-primary/[.6] hover:text-primary'
            }`}
            style={{
              color:
                active && themeStyle?.color ? themeStyle?.color : undefined,
            }}
          >
            <span className="px-5 font-500">
              {title}
              {count > 0 ? `(${count})` : ''}
            </span>
          </span>
          <p className="flex items-center mt-[2px]">
            {imageUrl && <Image src={imageUrl} alt="图标" />}
            {subTitle && (
              <span className="text-sm -leading-[1px] line-clamp-1">
                {subTitle}
              </span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export const TabPane: React.FC<ITabsPaneProps> = ({
  title,
  imageUrl,
  subTitle,
  children,
  count,
}) => {
  return children;
};

export const Tabs: React.FC<ITabsProps> = ({
  children,
  themeStyle,
  disabledAnimated = false,
  watchChildren = false,
}) => {
  const count = Children.count(children);
  const isMobile = useMedia('mobile');
  const { transitions, active, inViewRef, onDot, onStart, onStop, isStop } =
    useSpringCarousel({
      value: 0,
      duration: DURATION,
      total: count,
      stopAuto: isMobile,
      // disabled: true,
    });
  const { ref, height } = useChildHeightToParent(watchChildren, [active]);
  const handleClick = (i: number) => () => {
    onDot(i);
  };

  return (
    <div>
      {count > 1 && (
        <div
          ref={inViewRef as any}
          className="flex divide-x mb-6 divide-solid divide-primary/[.08] mobile:grid mobile:grid-cols-2 gap-y-px mobile:mb-3"
        >
          {Children.map(children, (child, i) => (
            <TabsTitle
              key={child.key}
              title={child.props.title}
              imageUrl={child.props.imageUrl}
              subTitle={child.props.subTitle}
              active={active === i}
              value={i}
              childrenCount={Children.count(children)}
              disabledAnimated={disabledAnimated}
              count={child.props.count}
              onClick={handleClick(i)}
              themeStyle={themeStyle}
              isStop={isStop}
              onStart={onStart}
              onStop={onStop}
            />
          ))}
        </div>
      )}
      <animated.div
        onMouseEnter={onStop}
        onMouseLeave={onStart}
        className="h-auto flex flex-nowrap relative overflow-hidden"
        style={{
          height: height.to((t) => t),
        }}
      >
        {transitions((style, i) => (
          <animated.div
            ref={ref}
            key={i}
            className="w-full absolute left-0 top-0"
            style={{
              ...style,
            }}
          >
            {Children.map(children, (child, j) =>
              active === j ? child.props.children : null
            )}
          </animated.div>
        ))}
      </animated.div>
    </div>
  );
};
